import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Alert } from 'src/domains/common/components/alert/alert';
import { Button } from 'src/domains/common/components/button/button';
import { Switch } from 'src/domains/common/components/switch/switch';
import Loading from 'src/components/Loading/Loading';
import { useAppStateContext } from 'src/AppState/AppState';
import { SelfExclusionConfigAudit } from 'src/components/ComplianceConfiguration/SelfExclusion/SelfExclusionConfigAudit';
import { SelfExclusionConfigState } from 'src/components/ComplianceConfiguration/SelfExclusion/SelfExclusion.state';
import {
    SelfExclusionActions,
    SelfExclusionFormLabel,
    SelfExclusionFormRow,
    SelfExclusionProperty,
    SelfExclusionPropertyList,
    SelfExclusionReadOnlyValue,
    SelfExclusionSection,
    SelfExclusionWrapper,
} from 'src/components/ComplianceConfiguration/SelfExclusion/SelfExclusion.style';

const formatHours = (hours: number): string => {
    return hours === 1 ? '1 hour' : `${hours} hours`;
};

export const SelfExclusionConfig = observer((): JSX.Element => {
    const appState = useAppStateContext();
    const [state] = useState(() => new SelfExclusionConfigState(appState));
    const [isAuditOpen, setIsAuditOpen] = useState(false);

    if (state.isLoading) {
        return (
            <SelfExclusionWrapper data-test='self-exclusion-config-loading'>
                <Loading active />
            </SelfExclusionWrapper>
        );
    }

    return (
        <SelfExclusionWrapper data-test='self-exclusion-config'>
            <Alert
                icon='info'
                title='Self exclusion'
                variant='default'
            >
                Cooling off and locked account properties are read-only. Manual exclusion email can be switched on or
                off. When it is on, the self-exclusion template is sent.
            </Alert>

            {state.errorMessage === null ? null : (
                <Alert
                    icon='info'
                    title={state.errorMessage}
                    variant='destructive'
                />
            )}

            {state.isReady ? (
                <SelfExclusionSection data-test='self-exclusion-config-form'>
                    {state.coolingOffHours === null ? null : (
                        <SelfExclusionFormRow>
                            <SelfExclusionFormLabel>Cooling off</SelfExclusionFormLabel>
                            <SelfExclusionReadOnlyValue data-test='self-exclusion-cooling-off-hours'>
                                {formatHours(state.coolingOffHours)}
                            </SelfExclusionReadOnlyValue>
                        </SelfExclusionFormRow>
                    )}

                    {state.lockedAccountProperties.length === 0 ? null : (
                        <SelfExclusionFormRow>
                            <SelfExclusionFormLabel>Locked account properties</SelfExclusionFormLabel>
                            <SelfExclusionPropertyList data-test='self-exclusion-locked-properties'>
                                {state.lockedAccountProperties.map((property) => (
                                    <SelfExclusionProperty key={property}>{property}</SelfExclusionProperty>
                                ))}
                            </SelfExclusionPropertyList>
                        </SelfExclusionFormRow>
                    )}

                    <SelfExclusionFormRow>
                        <SelfExclusionFormLabel>Manual exclusion email</SelfExclusionFormLabel>
                        <Switch
                            checked={state.manualExclusionEmailEnabled}
                            dataTest='self-exclusion-manual-email-enabled'
                            disabled={state.isSaving}
                            label={state.manualExclusionEmailEnabled ? 'Yes' : 'No'}
                            onChange={(event): void => {
                                state.setManualExclusionEmailEnabled(event.target.checked);
                            }}
                        />
                    </SelfExclusionFormRow>

                    <SelfExclusionActions>
                        <Button
                            dataTest='self-exclusion-save-configuration'
                            disabled={state.isSaving || !state.isDirty}
                            isLoading={state.isSaving}
                            onClick={(): void => {
                                void state.saveConfiguration();
                            }}
                            type='button'
                        >
                            Save configuration
                        </Button>
                    </SelfExclusionActions>
                </SelfExclusionSection>
            ) : null}

            <SelfExclusionActions>
                <Button
                    dataTest='self-exclusion-view-change-audit'
                    onClick={(): void => {
                        setIsAuditOpen(true);
                    }}
                    type='button'
                    variant='secondary'
                >
                    View change audit
                </Button>
            </SelfExclusionActions>

            {isAuditOpen ? (
                <SelfExclusionConfigAudit
                    onClose={(): void => {
                        setIsAuditOpen(false);
                    }}
                />
            ) : null}
        </SelfExclusionWrapper>
    );
});
